import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import CircularProgressIndicator from '@cartwhl/ui/messaging/CircularProgressIndicator';

import {
  animationGenerationStore,
  animationPlayerStore,
  noticesStore,
  sceneSettingsStore,
} from 'stores';
import { EXPORT_TYPES } from 'stores/animationGenerationStore';

import { useDownloadFreeInfo } from '../hooks';
import { exportAnimationFile } from '../utils';
import PlaceHolder from './PlaceHolder';
import VideoControls from './VideoControls';

export default function DownloadVideo({
  handleOpenChange,
}: {
  handleOpenChange: (open: boolean) => void;
}) {
  const {
    backgroundSettings: { canvasHeight, canvasWidth },
  } = useSnapshot(sceneSettingsStore.state);
  const { recordedVideo, isRecording, videoGenerationProgress } = useSnapshot(
    animationPlayerStore.state,
  );
  const { jobData } = useSnapshot(animationGenerationStore.state);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoUrl, setVideoUrl] = useState('');

  const { downloadIsFree } = useDownloadFreeInfo(EXPORT_TYPES.MP4);

  useEffect(() => {
    if (recordedVideo && recordedVideo.size > 0) {
      const url = URL.createObjectURL(recordedVideo);
      setVideoUrl(url);
    }
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [recordedVideo]);

  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.load();
    }
  }, [videoRef, videoUrl]);

  useEffect(() => {
    animationPlayerStore.startVideoRecording();

    return () => {
      setVideoUrl('');
      animationPlayerStore.resetVideoRecording();
    };
  }, []);

  // Determine what to show in the video area
  const renderVideoArea = () => {
    if (videoUrl) {
      return (
        <video
          className="max-h-[340px] max-w-full rounded-lg object-contain 2xl:max-h-[400px]"
          key={videoUrl}
          ref={videoRef}
          muted
          playsInline
          loop
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (isRecording || videoGenerationProgress) {
      return (
        <div className="flex flex-col items-center justify-center space-y-4 py-8">
          <CircularProgressIndicator
            progress={videoGenerationProgress?.percentage}
            className="mx-auto"
          />
          <Text variant="studio/body/sm" className="text-center">
            {videoGenerationProgress?.percentage
              ? `Generating video... ${Math.round(videoGenerationProgress.percentage)}%`
              : 'Preparing video generation...'}
          </Text>
          {videoGenerationProgress?.time && (
            <Text
              variant="studio/body/sm"
              className="text-gray-60 dark:text-gray-30"
            >
              Time: {videoGenerationProgress.time}
            </Text>
          )}
        </div>
      );
    }

    return <PlaceHolder text="Video download will begin shortly..." />;
  };

  return (
    <div className="flex flex-col justify-between space-y-6">
      <div>
        <div className="bg-gray-15 dark:bg-gray-75 flex items-center justify-center rounded-lg">
          {renderVideoArea()}
        </div>
        {videoUrl && (
          <div className="pt-4">
            <VideoControls videoRef={videoRef} videoUrl={videoUrl} />
          </div>
        )}
      </div>

      <div className="border-gray-30 dark:border-gray-75 grid grid-cols-2 gap-x-4 gap-y-6 border-t pt-6 sm:grid-cols-3">
        <Text variant="studio/body/sm">Canvas size:</Text>
        <div className="flex items-center">
          <Text className="text-gray-60" variant="studio/body/sm">
            {canvasWidth}x{canvasHeight}
          </Text>
        </div>
      </div>

      <div className="border-gray-30 dark:border-gray-75 dark:bg-gray-90 sticky -bottom-4 z-10 -mx-4 flex justify-between rounded-b-2xl border-t bg-white px-4 pt-4 pb-4 sm:mx-0 sm:px-0 md:pb-0">
        <Button onClick={() => handleOpenChange(false)}>Close</Button>
        <Button
          variant={recordedVideo || downloadIsFree ? 'primary' : 'secondary'}
          id="download-video-export"
          onClick={() => {
            if (!recordedVideo) {
              noticesStore.error('Please wait for video generation');
              return;
            }
            exportAnimationFile(videoUrl, 'mp4', jobData?.prompt);
          }}
        >
          {downloadIsFree ? 'Download (for free)' : 'Download'}
        </Button>
      </div>
    </div>
  );
}
